import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shadcnComponents/ui/alert-dialog";

import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shadcnComponents/ui/dialog";
import { Button } from "@/shadcnComponents/ui/button";

import { useAuth } from "@/Providers/Auth";
import { useCookies } from "react-cookie";
import axios from "axios";
import { ArrowRight, CircleCheckBig, LoaderCircle } from "lucide-react";
import { Checkbox } from "@/shadcnComponents/ui/checkbox";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import {
  addParamsToUrl,
  ExtensionPopupContentProps,
} from "../../PricingCalculatorHelpers/PriceCalHelpers";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "../../../../../Components/Navbar/Navbar";
import { cn } from "../../../../../lib/utils";
import { Footer } from "../../../LocationChapters/Components/organisms";
import { GradientDivider } from "../../../LocationChapters/Components/atoms";

const host = import.meta.env.VITE_BACKEND_SERVER_URL;
const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID;
const EXTENSION_REQUIRED_VERSION = import.meta.env
  .VITE_EXTENSION_REQUIRED_VERSION;

export const ExtensionDialog = ({
  open,
  onOpenChange,
  content,
  Trigger,
}: {
  Trigger?: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: React.ReactNode;
}) => {
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {Trigger && <DialogTrigger asChild>{Trigger}</DialogTrigger>}
        <DialogContent className="rounded-2xl md:rounded-2xl text-white border-none  shadow-[0px_10px_20px_0px_rgba(0,0,0,0.10)] bg-[#1B1F23] max-w-lg">
          {content}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ExtensionPopupContent: React.FC<ExtensionPopupContentProps> = ({
  reSync = false,
  redirectUrl,
  extTargetUrl,
  closeCurrentWindow = false,
  targetStartPage = "profile",
  otherRedirectParams = {},
  open,
  checkType = "MediaKitFlow",
}) => {
  const navigate = useNavigate();
  const authstate = useAuth();
  const [, setCookies] = useCookies();
  const [checked, setChecked] = useState(false);
  const [extensionMetaData, setExtensionMetaData] = useState({
    installedVersion: null,
    hasInstalled: false,
    updateRequired: false,
    extensionId: EXTENSION_ID,
    requiredVersion: EXTENSION_REQUIRED_VERSION,
  });
  const [loading, setLoading] = useState(false);
  const [isExtSupported, setIsExtSupported] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (checkType === "GuestFlow") {
      setChecked(localStorage.getItem("extensionConsent") === "true");
    } else if (checkType === "MediaKitFlow") {
      setChecked(authstate?.loggedUser?.extensionConsent);
    }
  }, [authstate?.loggedUser?.extensionConsent, checkType]);

  // Signal extension to check if it is installed
  useEffect(() => {
    if (chrome && chrome.runtime && chrome.runtime.sendMessage) {
      setIsExtSupported(true);
      const jwtToken = localStorage.getItem("jwtToken");
      const UserType = localStorage.getItem("UserType");
      chrome.runtime.sendMessage(
        extensionMetaData?.extensionId,
        { message: "version", data: { jwtToken, UserType } },
        (reply) => {
          if (chrome.runtime.lastError) {
            console.error("Error: No response from the extension");
            setExtensionMetaData({ ...extensionMetaData, hasInstalled: false });
          } else if (reply && reply.version) {
            if (reply.version >= extensionMetaData?.requiredVersion) {
              setExtensionMetaData({
                ...extensionMetaData,
                hasInstalled: true,
                installedVersion: reply.version,
              });
            }
            if (reply.version < extensionMetaData?.requiredVersion) {
              setExtensionMetaData({
                ...extensionMetaData,
                hasInstalled: true,
                installedVersion: reply.version,
                updateRequired: true,
              });
            }
          } else {
            setExtensionMetaData({ ...extensionMetaData, hasInstalled: false });
          }
        }
      );
    } else {
      setIsExtSupported(false);
      console.error("Chrome runtime messaging API is not available.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send trigger to extension
  const sendTriggerToExtension = async (payload) => {
    return new Promise((resolve) => {
      try {
        chrome.runtime.sendMessage(
          extensionMetaData?.extensionId,
          { message: "trigger", data: payload },
          (reply) => {
            if (chrome.runtime.lastError) {
              console.error("Error: No response from the extension");
              resolve({
                success: false,
                message: "Failed to trigger extension",
              });
            } else if (reply && reply.success) {
              resolve({
                success: true,
                message: "Extension triggered successfully",
                data: reply,
              });
            } else {
              resolve({
                success: false,
                message: "Failed to trigger extension",
              });
            }
          }
        );
      } catch (error) {
        console.error("Extension Error:", error);
        resolve({ success: false, message: "Failed to trigger extension" });
        setLoading(false);
      }
    });
  };

  const handleGuestContinue = async () => {
    try {
      if (isExtSupported) {
        setLoading(true);
        localStorage.setItem("extensionConsent", checked ? "true" : "false");
        const code = Math.random().toString(36).substring(7);

        try {
          // ---------------------- extension payload for Guest Flow ----------------------
          const expiryMinutes = 5; // in minutes
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const extReply: any = await sendTriggerToExtension({
            page: targetStartPage,
            redirect_url:
              redirectUrl +
              "?" +
              new URLSearchParams({ ...otherRedirectParams }).toString(),
            ref: "anchorsGuest",
            code: code,
            expiry: Date.now() + 1000 * 60 * expiryMinutes,
            expiresIn: expiryMinutes,
          });

          if (extReply?.success) {
            const url = addParamsToUrl(extTargetUrl, { code });
            window.location.href = url;
            // window.open(url, "_blank");
          } else {
            toast({
              title: "Failed to trigger extension",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error("Failed to trigger extension:", error);
        } finally {
          setTimeout(() => {
            setLoading(false);
          }, 1000);
        }
      } else {
        toast({
          title: "Extension is not supported",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to update extension consent:", error);
      setLoading(false);
    }
  };

  const handleUserContinue = async () => {
    try {
      if (!authstate?.loggedUser?.extensionConsent) {
        const res = await axios.post(
          `${host}/influencer/extension/consent`,
          { extensionConsent: checked },
          {
            headers: {
              jwtToken: localStorage.getItem("jwtToken"),
            },
          }
        );
        if (res?.data?.success) {
          console.log("Extension consent updated successfully");
        }
      }

      const generateLinkedInUrl = ({ params }: { params?: object }) => {
        const code = Math.random().toString(36).substring(7);

        const url = "https://www.linkedin.com/in/me/";
        const baseParams = {
          ref: "anchors",
          code: code,
          page: "profile",
        };
        const queryParams = new URLSearchParams({
          ...baseParams,
          ...params,
        });

        return `${url}?${queryParams.toString()}`;
      };

      const extParams = {
        redirect_url:
          redirectUrl || `${window.location.origin}/influencer/dashboard`,
        "preview-data": "true",
      };

      const getUpdatedData = async () => {
        const res = await axios.get(`${host}/ext/linkedin/me`, {
          headers: { jwtToken: localStorage.getItem("jwtToken") },
        });

        if (res?.data?.success) {
          // console.log(res?.data);
          if (
            res?.data?.data?.profile?.name &&
            res?.data?.data?.posts?.length
          ) {
            navigate("/influencer/dashboard/?preview-data=true");
          } else {
            window.open(
              extTargetUrl || generateLinkedInUrl({ params: extParams }),
              "_blank"
            );
          }
        } else {
          window.open(
            extTargetUrl || generateLinkedInUrl({ params: extParams }),
            "_blank"
          );
        }
      };

      if (reSync) {
        const url = extTargetUrl || generateLinkedInUrl({ params: extParams });
        window.open(url, "_blank");
      } else {
        getUpdatedData();
      }
    } catch (error) {
      console.error("Failed to update extension consent:", error);
    } finally {
      authstate?.setReFetchUserData((prev) => {
        return !prev;
      });
    }
  };

  const handleContinue = () => {
    if (checkType === "GuestFlow") {
      handleGuestContinue();
    } else if (checkType === "MediaKitFlow") {
      handleUserContinue();
    }
  };

  const handleInstallExtension = async () => {
    setCookies("extscurl", window.location.href, { path: "/" });
    // console.log(cookies?.extscurl, window.location.href);
    const url = `https://chromewebstore.google.com/detail/linkedin-media-kit-genera/${extensionMetaData?.extensionId}`;
    if (extensionMetaData?.hasInstalled) {
      if (extensionMetaData?.updateRequired) {
        window.open(url, "_blank"); // Update Extension
      } else {
        // Continue
        handleContinue();
      }
    } else {
      window.open(url, "_blank"); // Install Extension
    }

    if (closeCurrentWindow) {
      // console.log("Closing window");
      setTimeout(() => {
        window.close();
      }, 3000);
    }
  };

  useEffect(() => {
    let isMounted = true;
    async function continueNext() {
      try {
        const consent =
          checkType === "GuestFlow"
            ? localStorage.getItem("extensionConsent") === "true"
            : checkType === "MediaKitFlow"
            ? authstate?.loggedUser?.extensionConsent
            : false;
        if (
          extensionMetaData?.hasInstalled &&
          !extensionMetaData?.updateRequired &&
          consent
        ) {
          setLoading(true);
          await new Promise<void>((resolve) => {
            setTimeout(async () => {
              if (isMounted && open) {
                await handleInstallExtension();
              }
              resolve();
            }, 3000);
          });
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error("Failed to update extension consent:", error);
        setLoading(false);
      }
    }

    if (open) {
      continueNext();
    }
    return () => {
      isMounted = false; // Set the flag to false when the component unmounts
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    extensionMetaData?.hasInstalled,
    extensionMetaData?.updateRequired,
    open,
  ]);

  return (
    <>
      {loading && (
        <div className="p-4 text-white flex gap-2 items-center">
          <LoaderCircle className="animate-spin" />
          <span>Loading...</span>
        </div>
      )}
      {!loading && (
        <div className="text-white space-y-4">
          <h1 className="text-[#FFF] font-[Inter] text-base not-italic font-bold leading-[24px]">
            {extensionMetaData?.hasInstalled
              ? extensionMetaData?.updateRequired
                ? "Update Extension"
                : "Extension Installed"
              : "Install Extension"}
            <span className="text-muted-foreground text-xs">
              {extensionMetaData?.hasInstalled
                ? extensionMetaData?.updateRequired
                  ? ` >=(v${extensionMetaData?.requiredVersion})`
                  : ` (v${extensionMetaData?.installedVersion})`
                : ""}
            </span>
          </h1>

          {(!(checkType === "GuestFlow"
            ? localStorage.getItem("extensionConsent") === "true"
            : checkType === "MediaKitFlow"
            ? authstate?.loggedUser?.extensionConsent
            : false) ||
            !extensionMetaData?.hasInstalled) && (
            <>
              <div className="grid gap-4">
                <div className="flex flex-col items-start gap-4">
                  <ol className="list-disc text-[#A3A3A3] font-[Inter] text-sm not-italic font-normal leading-[20px] space-y-1">
                    <li className="ms-4">
                      <b>anchors</b> is your digital space to monetize your
                      content and grow together as part of a supportive
                      community of peers. With features like Collaboration Feed,
                      Revenue Sharing, & Free Tools needed for content creation,
                      you're all set.
                    </li>
                    <li className="ms-4">
                      To make the most out of <b> anchors</b>, we need your
                      consent to sync your LinkedIn posts and profile. Learn
                      more about permissions{" "}
                      <span
                        className="hover:underline text-[#DC2627] cursor-pointer"
                        onClick={() =>
                          window.open(
                            "https://www.anchors.in/privacy-policy",
                            "_blank"
                          )
                        }
                      >
                        here.
                      </span>
                    </li>
                  </ol>
                </div>
              </div>
            </>
          )}
          <div className="space-y-4">
            {(!(checkType === "GuestFlow"
              ? localStorage.getItem("extensionConsent") === "true"
              : checkType === "MediaKitFlow"
              ? authstate?.loggedUser?.extensionConsent
              : false) ||
              !extensionMetaData?.hasInstalled) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="terms"
                  onCheckedChange={(value) => setChecked(!!value)}
                  checked={checked}
                  className="border-[#fff]"
                />
                <label
                  htmlFor="terms"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I understand and give permission to <b>anchors</b> to sync my
                  LinkedIn posts and profile.
                </label>
              </div>
            )}
            <div className="flex justify-end">
              <Button
                variant="secondary"
                className="w-full rounded-full"
                onClick={handleInstallExtension}
                disabled={!checked}
              >
                {extensionMetaData?.hasInstalled
                  ? extensionMetaData?.updateRequired
                    ? "Update Extension"
                    : "Continue"
                  : "Install Extension"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const SuccessAlert = ({
  trigger,
  open,
  onOpenChange,
  onClickViewPrice,
}: {
  trigger?: React.ReactNode;
  open: boolean;
  onOpenChange: (boolean) => void;
  onClickViewPrice?: () => void;
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent className="text-white border-none  shadow-[0px_10px_20px_0px_rgba(0,0,0,0.10)] rounded-lg bg-[#1B1F23] max-w-lg">
        <AlertDialogHeader className="hidden">
          <AlertDialogTitle>Success</AlertDialogTitle>
        </AlertDialogHeader>
        <SuccessPopupContent onClickView={onClickViewPrice} />
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const PCPageBody = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex min-h-screen items-center flex-col bg-[#171717] text-white/70">
      <Navbar bg={"#171717"} />
      <main className="relative flex flex-col items-center overflow-hidden justify-center md:max-w-[1580px]  w-full min-h-[90vh] pb-5">
        <img
          className="w-[436px] h-[436px] top-0 -right-48 absolute animate-pulse "
          src="https://collab.anchors.in/FeatredCreatorBackground.png"
          alt=""
        />
        <img
          className="w-[436px] h-[436px] bottom-0 -left-48 absolute animate-pulse "
          src="https://collab.anchors.in/FeatredCreatorBackground.png"
          alt=""
        />
        {children}
      </main>
    </div>
  );
};

export const PostFormatTabsChip = ({
  text,
  active = false,
  onClick,
}: {
  text: string;
  active?: boolean;
  onClick?: () => void;
}) => {
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-full justify-center w-fit px-8 py-1 cursor-pointer",
        active
          ? "bg-[#D4D4D4] border border-[#D4D4D4]"
          : "bg-transparent border border-[#262626] hover:bg-[#262626]"
      )}
      onClick={onClick}
    >
      <span
        className={cn(
          "font-[Inter] text-xs not-italic font-medium leading-[24px]",
          active ? "text-[#000000]" : "text-[#A3A3A3]"
        )}
      >
        {text}
      </span>
    </div>
  );
};

export const ProgressBar = ({ progress }) => {
  return (
    <div className="w-full bg-[#262626] rounded-full h-2.5 dark:bg-gray-700">
      <div
        className="bg-red-600 h-2.5 rounded-full dark:bg-red-500"
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );
};

export const PopupHeader = ({ title, description, logo }) => {
  return (
    <>
      {title && (
        <h1 className="text-center font-[Inter] text-2xl not-italic font-semibold leading-[24px]">
          {title}
        </h1>
      )}
      {logo}
      {description && (
        <p className="text-[#D7D7D7] text-center font-[Inter] text-sm not-italic font-normal leading-[22px]">
          {description}
        </p>
      )}
    </>
  );
};

export const PopupSubSection = ({ title, description }) => {
  return (
    <>
      <div className="bg-[#BBBBBB1F] rounded-md px-4 py-3 flex flex-col gap-2 w-full">
        {title && (
          <h2 className="text-center font-[Inter] not-italic font-semibold leading-[24px]">
            {title}
          </h2>
        )}
        {description && (
          <p className="text-[#E2E8F0] text-center font-[Inter] text-xs not-italic font-normal leading-[20px]">
            {description}
          </p>
        )}
      </div>
    </>
  );
};

export const SuccessPopupContent = ({ onClickView }) => {
  return (
    <div className="flex flex-col gap-6 justify-center items-center">
      <PopupHeader
        title={"Analysis Complete!"}
        description={
          "We've analyzed your LinkedIn profile — your precise earnings estimate is ready"
        }
        logo={
          <div className="w-full flex justify-center items-center">
            <CircleCheckBig size={90} />
          </div>
        }
      />

      <div className="">
        <Button
          variant="destructive"
          size="sm"
          className="px-8 items-center gap-2 animate-bounce"
          onClick={onClickView}
        >
          <span>Reveal Now</span>
          <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
};
